import { TextSearchInput } from '@/app/components/TextSearchInput'
import { useSearchInputContext } from '@/app/modules/course/contexts/InputSearchContext'
import { useIntersectionObserver } from '@/app/modules/course/hooks/useIntersectionObserver'
import { useTranslations } from 'next-intl'
import Image from 'next/image'
import { useEffect, useRef } from 'react'

type CoursesHeaderProps = {
  onFilter: (term: string) => void
  onSearch: (searchTerm: string) => void
  wrapperProps?: React.HTMLProps<HTMLDivElement>
} & React.HTMLProps<HTMLDivElement>

export const CoursesHeader = ({
  wrapperProps,
  onSearch,
  onFilter,
  ...props
}: CoursesHeaderProps) => {
  const t = useTranslations('Courses')
  const searchInputRef = useRef<HTMLDivElement>(null)
  const isVisible = useIntersectionObserver(searchInputRef)
  const { setIsLargeSearchVisible } = useSearchInputContext()

  useEffect(() => {
    setIsLargeSearchVisible(isVisible)
  }, [isVisible, setIsLargeSearchVisible])

  return (
    <div {...wrapperProps} className={`${wrapperProps?.className ?? ''}`}>
      <div
        {...props}
        className={`flex flex-col gap-10 ${props.className ?? ''}`}
      >
        {/* Hero search section */}
        <div
          className="relative flex min-h-[250px] flex-col items-center justify-center gap-6 overflow-hidden bg-mydra-light-purple px-6 py-10 md:min-h-[460px] md:gap-16 md:rounded-xl md:px-16 md:py-20"
          style={{
            backgroundImage: 'url(/images/courses-hero-bg.png)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          {/* Background overlay */}
          <div className="pointer-events-none absolute inset-0 bg-[rgba(0,0,0,0.2)] md:rounded-xl md:bg-[rgba(17,17,17,0.4)]" />

          {/* Content */}
          <div className="relative z-10 flex w-full flex-col items-center gap-6 md:w-[856px] md:gap-12">
            <h1 className="w-[316px] text-center font-title text-2xl leading-[1.3] tracking-[-0.48px] text-white md:w-full md:text-[40px] md:leading-[1.2] md:tracking-[-0.8px]">
              {t('text_search_title')}
            </h1>

            <div ref={searchInputRef} className="w-[336px] md:w-[593px]">
              <TextSearchInput onSearch={onSearch} onFilterClick={onFilter} />
            </div>
          </div>

          {/* University logos - only visible on desktop */}
          <div className="relative z-10 hidden flex-row flex-wrap items-center justify-center gap-[42px] md:flex">
            <Image
              src="/images/henry-lg-white.png"
              alt="Henry"
              width={77}
              height={36}
              className="h-auto w-[77px] object-contain"
              priority
            />
            <Image
              src="/images/hult-lg-white.png"
              alt="Hult"
              width={77}
              height={36}
              className="h-auto w-[77px] object-contain"
              priority
            />
            <Image
              src="/images/IE-university-lg-white.png"
              alt="IE University"
              width={63}
              height={38}
              className="h-auto w-[63px] object-contain"
              priority
            />
            <Image
              src="/images/integra-lg-white.png"
              alt="Integra"
              width={95}
              height={58}
              className="h-auto w-[95px] object-contain"
              priority
            />
            <Image
              src="/images/iebs-white.png"
              alt="IEBS"
              width={95}
              height={58}
              className="h-auto w-[95px] object-contain"
              priority
            />
            <Image
              src="/images/CEI-lg-white.png"
              alt="CEI"
              width={117}
              height={74}
              className="h-auto w-[117px] object-contain"
              priority
            />
          </div>
        </div>
      </div>
    </div>
  )
}
